/** Resolves iOS asset names to /media URLs through the manifest written by tools/export-xcassets.mjs. */
import { config } from './config';

/** Shape written by tools/export-*.mjs (see play/tools/README.md). Paths are relative to /media/. */
export interface MediaManifest {
  images: Record<string, {
    file: string; width: number; height: number; scale: number; source?: string; vector?: boolean; locales?: Record<string, string>;
    /** 480 px wide variant (originals wider than 600 px), for srcset. */
    file480?: string;
    locales480?: Record<string, string>;
    /** 240 px wide variant (avatars/portraits wider than 300 px). */
    file240?: string;
    locales240?: Record<string, string>;
  }>;
  data?: Record<string, string[]>;
  audio?: Record<string, { file: string; ext: string; locale?: string; mp3?: string; resource?: string }>;
  pdf?: Record<string, { file: string; locale?: string }>;
  video?: Record<string, { file: string; locale?: string }>;
  lottie?: Record<string, { file: string }>;
}

let manifest: MediaManifest | undefined;
let pending: Promise<MediaManifest> | undefined;

export function loadManifest(): Promise<MediaManifest> {
  pending ??= fetch(`${config.mediaBase}/manifest.json`).then(async (r) => { manifest = (await r.json()) as MediaManifest; return manifest; });
  return pending;
}

const LOCALE_SUFFIX = /(?:_([a-z]{2}(?:_[A-Z]{2})?)|\.([a-z]{2}(?:-[A-Z]{2})?))$/;

type ImageEntry = MediaManifest['images'][string];

/** Locale rule: exact name, else base name + locales[locale], else base file. */
function resolveImageFile(assetName: string, locale?: string): { entry: ImageEntry; file: string; w480?: string; w240?: string } | undefined {
  const images = manifest?.images;
  if (!images) return undefined;
  const pick = (entry: ImageEntry, loc: string | undefined) => {
    const localized = loc ? entry.locales?.[loc] : undefined;
    return localized
      ? { entry, file: localized, w480: entry.locales480?.[loc!], w240: entry.locales240?.[loc!] }
      : { entry, file: entry.file, w480: entry.file480, w240: entry.file240 };
  };
  const exact = images[assetName];
  if (exact) return pick(exact, locale);
  const base = assetName.replace(LOCALE_SUFFIX, '');
  const m = images[base];
  if (!m) return undefined;
  const suffix = assetName.match(LOCALE_SUFFIX);
  return pick(m, (suffix?.[1]?.replace('_', '-') ?? suffix?.[2]) || locale);
}

export function imageURL(assetName: string, locale?: string): string | undefined {
  const r = resolveImageFile(assetName, locale);
  return r ? `${config.mediaBase}/${r.file}` : undefined;
}

/**
 * `srcset` for an asset with responsive variants (`240w` avatars, `480w`, + original width), else undefined.
 * Pair it with `sizes` (`auto` for lazy images, or the rendered width).
 */
export function imageSrcSet(assetName: string, locale?: string): string | undefined {
  const r = resolveImageFile(assetName, locale);
  if (!r || (!r.w480 && !r.w240)) return undefined;
  const b = config.mediaBase;
  return [r.w240 && `${b}/${r.w240} 240w`, r.w480 && `${b}/${r.w480} 480w`, `${b}/${r.file} ${r.entry.width}w`].filter(Boolean).join(', ');
}
export function imageMeta(assetName: string) { return manifest?.images[assetName]; }
/** Prefers the mp3 transcode when the source is m4a (Firefox/Linux safety). */
export function audioURL(key: string): string | undefined { const a = manifest?.audio?.[key]; if (!a) return undefined; return `${config.mediaBase}/${a.mp3 ?? a.file}`; }
export function pdfURL(key: string): string | undefined { const f = manifest?.pdf?.[key]?.file; return f ? `${config.mediaBase}/${f}` : undefined; }
export function videoURL(key: string): string | undefined { const f = manifest?.video?.[key]?.file; return f ? `${config.mediaBase}/${f}` : undefined; }
export function lottieURL(key: string): string | undefined { const f = manifest?.lottie?.[key]?.file; return f ? `${config.mediaBase}/${f}` : undefined; }
export function dataFiles(datasetName: string): string[] { return (manifest?.data?.[datasetName] ?? []).map((f) => `${config.mediaBase}/${f}`); }
